import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Award, Leaf, Heart, Star } from 'lucide-react';

const FAQS = [
  {
    q: "Where exactly is Rudragarh Resort located?",
    a: "Rudragarh Resort is in Damodra village on Sam Road, about 21 km from Jaisalmer city, the railway station, and the airport. We are set within the golden Thar Desert, offering both privacy and easy access to Jaisalmer's forts, havelis, and cultural sites."
  },
  {
    q: "What makes Rudragarh different from other resorts in Jaisalmer?",
    a: "Rudragarh is a boutique property — intimate, owner-managed, and designed to feel like a private desert estate rather than a hotel. Our signature cave-side stays, private sand dunes, natural pond and waterfall, and a full range of on-property adventures make us a complete destination in ourselves."
  },
  {
    q: "Is Rudragarh suitable for families with children?",
    a: "Absolutely. We have a Kids' Play Zone, a swimming pool, cycling tracks, football and archery — plenty to keep children engaged. The Catacomb Suite and The Heritage Huts are especially popular with families looking for a unique experience."
  },
];

const About = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>About Rudragarh Resort | Boutique Desert Retreat near Jaisalmer</title>
        <meta name="description" content="Learn the story behind Rudragarh Resort — a boutique desert retreat near Sam Sand Dunes, Jaisalmer. Heritage architecture, private dunes, and authentic Rajasthani hospitality." />
        <meta property="og:title" content="About Rudragarh Resort | Boutique Desert Retreat near Jaisalmer" />
        <meta property="og:description" content="Learn the story behind Rudragarh Resort — a boutique desert retreat near Sam Sand Dunes, Jaisalmer. Heritage architecture, private dunes, and authentic Rajasthani hospitality." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/about" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Rudragarh Resort | Boutique Desert Retreat near Jaisalmer" />
        <meta name="twitter:description" content="Learn the story behind Rudragarh Resort — a boutique desert retreat near Sam Sand Dunes, Jaisalmer." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Rudragarh Resort",
          "url": "https://www.rudragarh.com/about",
          "description": "The story and philosophy behind Rudragarh Resort, a boutique desert retreat near Sam Sand Dunes, Jaisalmer, Rajasthan.",
          "mainEntity": {
            "@type": "LodgingBusiness",
            "name": "Rudragarh Resort",
            "url": "https://www.rudragarh.com",
            "description": "Boutique desert retreat near Sam Sand Dunes, Jaisalmer offering cave stays, heritage cottages, bamboo duplex huts, and luxury desert experiences.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Village Damodra, Sam Road",
              "addressLocality": "Jaisalmer",
              "addressRegion": "Rajasthan",
              "postalCode": "345001",
              "addressCountry": "IN"
            }
          },
          "mainEntityOfPage": {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQS.map(f => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a }
            }))
          }
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/40 to-void" />
        <div className="relative z-10 text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="overline">Our Story</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              About <span className="text-gold-500">Rudragarh</span>
            </h1>
            <p className="text-foreground/70 max-w-xl mx-auto text-lg">
              A desert sanctuary where heritage meets adventure, and every guest feels at home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">The Beginning</span>
              <h2 className="text-3xl md:text-4xl font-heading font-medium">
                Born from the <span className="text-gold-500">Desert</span>
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                Rudragarh Resort was built on a single conviction: that the Thar Desert,
                one of India's most dramatic landscapes, deserved a retreat worthy of its grandeur.
                Set in Damodra village on the road to the Sam Sand Dunes, the resort was built to
                blend seamlessly into the desert — earthy materials, open skies, and the silence
                of the dunes at night.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                Every structure on the property has been built to honour the region's
                architectural heritage while offering the comforts of a modern luxury retreat.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/images/about-horses-lawn.jpeg"
                alt="Marwari horses on the lawns of Rudragarh Resort"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-gold-500 text-black p-6">
                <p className="font-heading text-lg">Damodra, Sam Road</p>
                <p className="text-sm opacity-80">21 km from Jaisalmer</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Cards */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="overline">What We Stand For</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Our <span className="text-gold-500">Philosophy</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Heart size={28} />, title: "Warm Hospitality", desc: "Every guest is family. Our team is trained to anticipate needs before they're spoken." },
              { icon: <Leaf size={28} />, title: "Desert Harmony", desc: "Built with local materials and designed to disturb the landscape as little as possible." },
              { icon: <Award size={28} />, title: "Authentic Experiences", desc: "From folk performances to ATV rides, every experience is rooted in Rajasthani culture." },
              { icon: <Star size={28} />, title: "Boutique Intimacy", desc: "A small property by design — so every guest receives personal attention and privacy." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{item.icon}</div>
                <h3 className="font-heading text-xl">{item.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Property Overview */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="/images/about-gate.jpeg"
                alt="Rudragarh Resort pool"
                className="w-full h-52 object-cover"
              />
              <img
                src="/images/about-horse-garden.jpeg"
                alt="A Marwari horse in the gardens of Rudragarh"
                className="w-full h-52 object-cover mt-8"
              />
              <img
                src="/images/resort-theatre.jpg"
                alt="Open air theatre"
                className="w-full h-52 object-cover"
              />
              <img
                src="/images/about-gate.jpeg"
                alt="The gate of Rudragarh Resort"
                className="w-full h-52 object-cover mt-8"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">The Property</span>
              <h2 className="text-3xl md:text-4xl font-heading font-medium">
                A Complete <span className="text-gold-500">Desert Estate</span>
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                Spread across the desert at Damodra on Sam Road, Rudragarh is designed as a
                self-contained escape. Guests rarely need to leave the property — with a swimming pool, gym,
                dining hall, open kitchen, bar, private sand dunes, and a natural pond and
                waterfall all on-site.
              </p>
              <ul className="grid grid-cols-2 gap-3">
                {[
                  "Swimming Pool", "Fully Equipped Gym", "Grand Dining Hall",
                  "Live Open Kitchen", "Bar Room", "Private Sand Dunes",
                  "Natural Pond & Waterfall", "Horse Riding", "ATV on the Dunes",
                  "Open-Air Theatre", "Cultural Programs", "Kids' Play Zone",
                ].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-foreground/70">
                    <span className="w-1.5 h-1.5 bg-gold-500 rounded-full flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">FAQ</span>
            <h2 className="text-3xl font-heading font-medium">
              Common <span className="text-gold-500">Questions</span>
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-foreground/70 leading-relaxed border-t border-gold-500/10 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-hut-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Your Desert Story Awaits</span>
          <h2 className="text-4xl font-heading font-medium mb-6">
            Come Experience <span className="text-gold-500">Rudragarh</span>
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Whether it's a romantic escape, a family adventure, or a group getaway —
            Rudragarh is ready to make it unforgettable.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book" className="btn-primary">Book Your Stay</Link>
            <Link to="/contact" className="btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
